import React from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { KeyRound, ArrowLeft, CheckCircle2 } from 'lucide-react'
import { apiPost } from '@/lib/api'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'

const schema = z.object({
  token: z.string().min(1, 'Token zorunludur'),
  newPassword: z
    .string()
    .min(8, 'En az 8 karakter olmalı')
    .regex(/[A-Z]/, 'En az bir büyük harf içermeli')
    .regex(/[0-9]/, 'En az bir rakam içermeli'),
  confirmNewPassword: z.string(),
}).refine(d => d.newPassword === d.confirmNewPassword, {
  message: 'Şifreler eşleşmiyor',
  path: ['confirmNewPassword'],
})
type FormData = z.infer<typeof schema>

export const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [done, setDone] = React.useState(false)
  const [serverError, setServerError] = React.useState('')

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { token: searchParams.get('token') ?? '' },
  })

  const onSubmit = async (data: FormData) => {
    setServerError('')
    try {
      await apiPost('/auth/reset-password', {
        token: data.token,
        newPassword: data.newPassword,
        confirmNewPassword: data.confirmNewPassword,
      })
      setDone(true)
      setTimeout(() => navigate('/giris', { replace: true }), 3000)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setServerError(msg ?? 'Token geçersiz veya süresi dolmuş.')
    }
  }

  return (
    <div className="min-h-dvh flex items-center justify-center bg-slate-950 p-4">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg">
            <KeyRound className="h-6 w-6 text-white" />
          </div>
        </div>

        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-8 shadow-card">
          {done ? (
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <CheckCircle2 className="h-12 w-12 text-green-400" />
              </div>
              <h1 className="text-xl font-bold text-slate-100 mb-2">Şifre Güncellendi</h1>
              <p className="text-sm text-slate-400 mb-2">
                Yeni şifreniz başarıyla ayarlandı.
              </p>
              <p className="text-xs text-slate-500">Giriş sayfasına yönlendiriliyorsunuz…</p>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="text-xl font-bold text-slate-100">Yeni Şifre Belirle</h1>
                <p className="text-sm text-slate-400 mt-1">
                  E-postanızdaki token'ı ve yeni şifrenizi girin.
                </p>
              </div>

              {serverError && (
                <div role="alert" className="mb-4 rounded-lg bg-red-900/20 border border-red-800/50 p-3 text-sm text-red-400">
                  {serverError}
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
                <Input
                  label="Sıfırlama Token'ı"
                  placeholder="E-postanızdaki token"
                  autoComplete="off"
                  error={errors.token?.message}
                  {...register('token')}
                />
                <Input
                  label="Yeni Şifre"
                  type="password"
                  placeholder="En az 8 karakter"
                  autoComplete="new-password"
                  error={errors.newPassword?.message}
                  {...register('newPassword')}
                />
                <Input
                  label="Yeni Şifre (Tekrar)"
                  type="password"
                  placeholder="Şifrenizi tekrar girin"
                  autoComplete="new-password"
                  error={errors.confirmNewPassword?.message}
                  {...register('confirmNewPassword')}
                />

                <Button type="submit" fullWidth loading={isSubmitting}>
                  {isSubmitting ? 'Kaydediliyor…' : 'Şifreyi Güncelle'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  to="/sifremi-unuttum"
                  className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Token almak için geri dön
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
